import { Box, Checkbox, Grid, Pagination, Typography } from "@mui/material";
import { AppGlobalClassKeys } from "app/appGlobalStyles";
import { MprcCardActions, MprcJoinComponents, MprcLink, MprcSpinnerIndicator, MprcTextMuted } from "components";
import { MprcFilterBox, MprcFilterLine } from "components/filter";
import { MprcFilterEmptyDataset } from "components/filter/MprcFilterEmptyDataset";
import { IconButtonViewDetails } from "components/iconButtons/IconButtonViewDetails";
import { IconSort } from "components/icons";
import { MprcIconButtonDropdown } from "components/MprcIconButtonDropdown";
import { MprcSearchTextField } from "components/MprcSearchTextField";
import { QuestionSelectListOrderByQuery, QuestionSelectListResponse, QuestionSelectListResponsePaginatedList } from "generated/CoreApi_v1";
import { useCoreApi } from "providers/CoreApiProvider";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { QuestionTypeText } from "views/assessment/questionBank/components/QuestionTypeText";
import { EditQuestion } from "views/assessment/questionBank/Home/EditQuestion/EditQuestion";

////////////////////////////////////////////////////////////////////////////

export interface IQuestionSelectFilterOptions {
    searchText: string;
    orderBy: QuestionSelectListOrderByQuery;
}

interface IViewDataFilter {
    pageNumber: number;
    filter: IQuestionSelectFilterOptions;
}

export interface IQuestionSelectedTotal {
    totalSelected: number;
    totalPoints: number;
}

interface IProps {
    questionGroupId: string;
    selected: string[];
    disabled: boolean;
    forSurvey: boolean;
    onlyKnowledgeTests?: boolean;

    onSelected: (selected: string[], total: IQuestionSelectedTotal) => void;
    onError: (error: any) => void;
}

////////////////////////////////////////////////////////////////////////////

export const QuestionsSelectListBox: React.FC<IProps> = (props) => {

    const { questionGroupId, selected, disabled, forSurvey, onlyKnowledgeTests = false, onSelected, onError } = props;

    const api = useCoreApi();
    const { t } = useTranslation("assessment");

    const [loading, setLoading] = useState<boolean>(false);
    const [showSubtitle, setShowSubtitle] = useState<boolean>(false);
    const [dialog, setDialog] = useState<any>();
    const viewData = useRef<QuestionSelectListResponsePaginatedList | null>(null);

    const [viewFilter, setViewFilter] = useState<IViewDataFilter>({
        pageNumber: 1,
        filter: {
            searchText: "",
            orderBy: QuestionSelectListOrderByQuery.Recent
        }
    });

    ///
    const filtered = viewFilter.filter.searchText.length > 0;

    ///
    const sumTotalPoints = (ids: string[], data: QuestionSelectListResponse[]) => {

        if (ids.length == 0) return 0;

        return data
            .filter(x => ids.some(p => p == x.questionId))
            .map(x => x.points)
            .reduce((accumulator, currentValue) => accumulator + currentValue);
    };

    ///
    const buildSecondLine = (question: QuestionSelectListResponse) => {
        var items: React.ReactNode[] = [];

        if (!forSurvey || question.points > 0)
            items.push(<Typography variant="caption">{t("QuestionsSelect.List.Points", { count: question.points })}</Typography>);

        items.push(<Typography variant="caption">{<QuestionTypeText questionType={question.questionType} />}</Typography>);

        return <Typography variant="caption"><MprcJoinComponents components={items} separator={<span>&nbsp;&bull;&nbsp;</span>} /></Typography>;
    };

    ///
    const isSelected = (id: string) => {
        return selected.findIndex(x => x == id) !== -1;
    };

    ///
    const handleSelect = (id: string) => {
        let newSelected = selected.map(x => x);
        if (selected.some(x => x == id)) {
            newSelected = newSelected.filter(x => x != id);
        }
        else {
            newSelected.push(id);
        }

        onSelected(newSelected, {
            totalSelected: newSelected.length,
            totalPoints: sumTotalPoints(newSelected, viewData.current?.items ?? [])
        });
    };

    ///
    const clearSelect = () => {
        onSelected([], {
            totalSelected: 0,
            totalPoints: 0
        });
    };

    ///
    const handleClick = (question: QuestionSelectListResponse) => {
        setDialog(<EditQuestion
            questionId={question.questionId}
            questionType={question.questionType}
            forSurvey={forSurvey}
            readonly
            onClose={() => setDialog(null)}
        />);
    };

    ////////////////////////////////////////////////////////////////////////////
    /// EFFECT'S
    ////////////////////////////////////////////////////////////////////////////

    // Ao selecionar um novo grupo
    useEffect(() => {
        setLoading(true);
    }, [questionGroupId]);

    // Ao carregar a lista
    useEffect(() => {
        async function getData() {
            try {
                if (questionGroupId == null) {
                    viewData.current = null;
                }
                else {
                    var result = await api.v1.questionBank.getQuestionSelect({
                        pageNumber: viewFilter.pageNumber,
                        pageSize: 50,
                        questionGroupId,
                        searchText: viewFilter.filter.searchText ?? null,
                        onlyAllowInKnowledgeTests: onlyKnowledgeTests ?? false,
                        orderBy: viewFilter.filter.orderBy
                    });
                    viewData.current = result.data;
                }
            } catch (e) {
                viewData.current = null;
                onError(e);
            } finally {
                clearSelect();
                setLoading(false);
            }
        }

        if (loading) getData();
    }, [loading]);

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    ///
    const Filter = () => {
        return (
            <MprcFilterBox>
                <MprcFilterLine>
                    <Grid container spacing={1} marginY={1} width="100%">
                        <Grid size="grow">
                            <MprcSearchTextField
                                confirmMode="debounce"
                                onConfirm={(value) => {
                                    setViewFilter({ ...viewFilter, pageNumber: 1, filter: { ...viewFilter.filter, searchText: value } });
                                    setLoading(true);
                                }}
                                placeholder={t("Question.List.SearchTextPlaceholder")}
                            />
                        </Grid>
                        {
                            !onlyKnowledgeTests &&
                            <Grid>
                                <IconButtonViewDetails
                                    color={showSubtitle ? "highlight" : "inherit"}
                                    onClick={() => setShowSubtitle(!showSubtitle)}
                                    disabled={disabled}
                                />
                                <MprcIconButtonDropdown
                                    menuItemCheck
                                    title={t("shared:Filter.OrderBy")}
                                    icon={<IconSort />}
                                    selected={viewFilter.filter.orderBy}
                                    disabled={disabled}
                                    onChange={(orderBy) => {
                                        setViewFilter({ ...viewFilter, filter: { ...viewFilter.filter, orderBy: orderBy } });
                                        setLoading(true);
                                    }}
                                    items={
                                        [
                                            { text: t("QuestionsSelect.List.OrderBy.Recent"), value: QuestionSelectListOrderByQuery.Recent },
                                            { text: t("QuestionsSelect.List.OrderBy.Point"), value: QuestionSelectListOrderByQuery.Point },
                                            { text: t("QuestionsSelect.List.OrderBy.Title"), value: QuestionSelectListOrderByQuery.Title }
                                        ]
                                    }
                                />
                            </Grid>
                        }
                    </Grid>
                </MprcFilterLine>
            </MprcFilterBox>
        );
    };

    ///
    const RenderGrid = (
        <Grid container justifyContent="center" alignItems="center">
            {
                viewData.current?.items.map((item, index) => {
                    return (
                        <Grid size={{ xs: 12 }} key={index} >
                            <Grid container alignItems="center">
                                <Grid>
                                    <Checkbox
                                        checked={isSelected(item.questionId)}
                                        disabled={disabled}
                                        onChange={() => handleSelect(item.questionId)}
                                    />
                                </Grid>
                                <Grid size="grow">
                                    <Grid container direction="column">
                                        <Grid>
                                            <Typography variant="h5">
                                                <MprcTextMuted>
                                                    <MprcLink
                                                        disabled={disabled}
                                                        onClick={() => handleClick(item)}
                                                    >
                                                        {item.questionText}
                                                    </MprcLink>
                                                </MprcTextMuted>
                                            </Typography>
                                        </Grid>
                                        {
                                            showSubtitle &&
                                            <Grid>
                                                <Grid container justifyContent="flex-start" alignItems="center" spacing={0}>
                                                    {buildSecondLine(item)}
                                                </Grid>
                                            </Grid>
                                        }
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    );
                })
            }
        </Grid>
    );

    ///
    return (
        <>
            {dialog}

            <Grid container direction="column" height="100%" spacing={1}>
                <Grid width="100%">

                    {Filter()}

                </Grid>

                <Grid size={{ xs: 12 }} width="100%" flexGrow={1} overflow="hidden">
                    <Grid container direction="column" height="100%">
                        <Grid size="grow" overflow="auto" className={AppGlobalClassKeys.thinScrollbar}>

                            {loading
                                ? <MprcSpinnerIndicator />
                                : filtered && viewData.current?.totalRows == 0
                                    ? <MprcFilterEmptyDataset message={t("QuestionsSelect.NoRecordsFoundMessage")} marginTop="normal" />
                                    : RenderGrid
                            }

                        </Grid>
                    </Grid>
                </Grid>

            </Grid>

            <Box mt={1}>
                <MprcCardActions>
                    {
                        !loading && viewData.current && viewData.current?.totalPages > 1 &&
                        <Pagination
                            shape="rounded"
                            color="primary"
                            page={viewData.current?.pageNumber}
                            count={viewData.current?.totalPages}
                            onChange={(e, pageNumber) => {
                                setViewFilter({ ...viewFilter, pageNumber });
                                setLoading(true);
                            }}
                        />
                    }
                </MprcCardActions>
            </Box>
        </>
    );
};
